import React, { useState, useEffect, useContext, useMemo } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import {
	Paper,
	IconButton,
	TextField,
	InputAdornment,
	Typography,
	Button,
	Tooltip,
	Collapse,
	Avatar,
	LinearProgress,
	Menu,
	MenuItem,
	ListItemIcon,
	Divider,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import RefreshIcon from "@material-ui/icons/Refresh";
import GetAppIcon from "@material-ui/icons/GetApp";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import DesktopWindowsIcon from "@material-ui/icons/DesktopWindows";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import BlockIcon from "@material-ui/icons/Block";
import LockOpenIcon from "@material-ui/icons/LockOpen";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

import DeviceId from "./DeviceId";
import StatusDot from "./StatusDot";

const INSTALLER_URL = "/download/flydesk-install.exe";

// Heartbeat do cliente é de 3–15 s; 2 min de folga evita piscar "offline"
// numa oscilação de rede.
const ONLINE_WINDOW_MS = 2 * 60 * 1000;

const useStyles = makeStyles((theme) => ({
	root: {
		flex: 1,
		overflowY: "auto",
		padding: theme.spacing(0, 0, 4),
		...theme.scrollbarStyles,
	},
	statsRow: {
		display: "grid",
		gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
		gap: theme.spacing(2),
		marginBottom: theme.spacing(3),
	},
	statCard: {
		padding: theme.spacing(2, 2.5),
		display: "flex",
		flexDirection: "column",
		gap: 6,
	},
	statValue: {
		fontSize: "1.9rem",
		fontWeight: 800,
		lineHeight: 1,
		letterSpacing: "-0.03em",
	},
	statLabel: {
		fontSize: "0.72rem",
		fontWeight: 700,
		textTransform: "uppercase",
		letterSpacing: "0.06em",
		color: theme.custom.textSecondary,
	},
	accentOnline: { color: theme.palette.success.main },
	accentBlocked: { color: theme.palette.error.main },

	installBox: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(2),
		padding: theme.spacing(2, 2.5),
		marginBottom: theme.spacing(3),
		background:
			theme.palette.type === "dark"
				? "linear-gradient(135deg, rgba(5,150,105,.16), rgba(14,165,233,.10))"
				: "linear-gradient(135deg, rgba(5,150,105,.09), rgba(14,165,233,.07))",
		border: `1px solid ${theme.custom.border}`,
	},
	installText: { flex: 1, minWidth: 0 },

	companyCard: {
		marginBottom: theme.spacing(2),
		overflow: "hidden",
	},
	companyHeader: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(2),
		padding: theme.spacing(1.75, 2.5),
		cursor: "pointer",
		transition: "background-color .15s ease",
		"&:hover": { backgroundColor: theme.custom.surfaceHover },
	},
	companyAvatar: {
		width: 40,
		height: 40,
		fontWeight: 700,
		fontSize: "0.95rem",
		color: "#fff",
	},
	companyInfo: { flex: 1, minWidth: 0 },
	companyName: {
		fontWeight: 700,
		fontSize: "1rem",
		lineHeight: 1.3,
	},
	companyMeta: {
		fontSize: "0.78rem",
		color: theme.custom.textSecondary,
		marginTop: 2,
	},
	licenceWrap: { width: 130, flexShrink: 0 },
	licenceBar: {
		height: 6,
		borderRadius: 4,
		backgroundColor:
			theme.palette.type === "dark" ? "rgba(148,163,184,.16)" : "#E2E8F0",
	},
	licenceBarFull: {
		"& .MuiLinearProgress-bar": {
			backgroundColor: theme.palette.warning.main,
		},
	},
	licenceLabel: {
		fontSize: "0.7rem",
		fontWeight: 600,
		color: theme.custom.textSecondary,
		marginBottom: 4,
		display: "block",
		textAlign: "right",
	},
	chevron: {
		transition: "transform .2s ease",
		color: theme.custom.textSecondary,
	},
	chevronOpen: { transform: "rotate(180deg)" },

	deviceRow: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(2),
		padding: theme.spacing(1.5, 2.5),
		borderTop: `1px solid ${theme.custom.border}`,
		transition: "background-color .15s ease",
		"&:hover": { backgroundColor: theme.custom.surfaceHover },
	},
	deviceRowBlocked: { opacity: 0.55 },
	deviceIcon: {
		color: theme.custom.textSecondary,
		fontSize: 22,
		flexShrink: 0,
	},
	deviceMain: { flex: 1, minWidth: 0 },
	deviceName: {
		fontWeight: 600,
		fontSize: "0.92rem",
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	},
	deviceSub: {
		fontSize: "0.75rem",
		color: theme.custom.textSecondary,
		marginTop: 1,
	},
	deviceStatus: { width: 118, flexShrink: 0 },
	deviceSeen: {
		width: 150,
		flexShrink: 0,
		fontSize: "0.76rem",
		color: theme.custom.textSecondary,
	},
	emptyCompany: {
		padding: theme.spacing(2.5),
		borderTop: `1px solid ${theme.custom.border}`,
		fontSize: "0.85rem",
		color: theme.custom.textSecondary,
		textAlign: "center",
	},
	emptyState: {
		padding: theme.spacing(8, 3),
		textAlign: "center",
		color: theme.custom.textSecondary,
	},
	emptyIcon: { fontSize: 46, opacity: 0.35, marginBottom: 12 },
	skeleton: {
		height: 76,
		marginBottom: theme.spacing(2),
		borderRadius: 12,
		backgroundColor: theme.custom.surfaceHover,
		animation: "$shimmer 1.4s ease-in-out infinite",
	},
	"@keyframes shimmer": {
		"0%, 100%": { opacity: 0.55 },
		"50%": { opacity: 1 },
	},
	search: { width: 260 },
}));

// Cor estável a partir do nome: a mesma empresa mantém sempre a mesma cor,
// o que ajuda a reconhecê-la de relance na lista.
const AVATAR_COLORS = [
	"#059669", "#0EA5E9", "#8B5CF6", "#F59E0B",
	"#EC4899", "#14B8A6", "#6366F1", "#EF4444",
];
const colorFor = (name) => {
	let hash = 0;
	for (let i = 0; i < (name || "").length; i += 1) {
		hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
	}
	return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

const initialsOf = (name) =>
	(name || "?")
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((w) => w[0])
		.join("")
		.toUpperCase();

const isOnline = (lastSeenAt) =>
	Boolean(lastSeenAt) &&
	Date.now() - new Date(lastSeenAt).getTime() < ONLINE_WINDOW_MS;

const relativeTime = (value) => {
	if (!value) return i18n.t("flydeskDevices.never");
	const diff = Date.now() - new Date(value).getTime();
	const min = Math.floor(diff / 60000);
	if (min < 1) return i18n.t("flydeskDevices.justNow");
	if (min < 60) return i18n.t("flydeskDevices.minutesAgo", { m: min });
	const h = Math.floor(min / 60);
	if (h < 24) return i18n.t("flydeskDevices.hoursAgo", { h });
	const d = Math.floor(h / 24);
	if (d < 30) return i18n.t("flydeskDevices.daysAgo", { d });
	return new Date(value).toLocaleDateString();
};

const FlydeskDevices = () => {
	const classes = useStyles();
	const { user } = useContext(AuthContext);
	const isSuper = Boolean(user?.super);
	const canManage = Boolean(user?.super || user?.profile === "admin");

	const [devices, setDevices] = useState([]);
	const [companies, setCompanies] = useState([]);
	const [loading, setLoading] = useState(true);
	const [searchParam, setSearchParam] = useState("");
	const [collapsed, setCollapsed] = useState({});
	const [menu, setMenu] = useState({ anchor: null, device: null });
	const [deletingDevice, setDeletingDevice] = useState(null);
	const [confirmOpen, setConfirmOpen] = useState(false);
	// Só para reavaliar online/offline sem nova requisição
	const [, setTick] = useState(0);

	const fetchDevices = async (silent = false) => {
		if (!silent) setLoading(true);
		try {
			const { data } = await api.get("/flydesk/devices");
			setDevices(data.devices || []);
			setCompanies(data.companies || []);
		} catch (err) {
			toastError(err);
		}
		if (!silent) setLoading(false);
	};

	useEffect(() => {
		fetchDevices();
		// Atualiza sozinho: o painel costuma ficar aberto enquanto se fala
		// com o cliente ao telefone, e um PC que acabou de subir precisa
		// aparecer sem F5.
		const poll = setInterval(() => fetchDevices(true), 30000);
		const clock = setInterval(() => setTick((t) => t + 1), 30000);
		return () => {
			clearInterval(poll);
			clearInterval(clock);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleToggleBlocked = async (device) => {
		setMenu({ anchor: null, device: null });
		try {
			await api.put(`/flydesk/devices/${device.id}`, {
				blocked: !device.blocked,
			});
			setDevices((prev) =>
				prev.map((d) =>
					d.id === device.id ? { ...d, blocked: !d.blocked } : d
				)
			);
			toast.success(i18n.t("flydeskDevices.toasts.updated"));
		} catch (err) {
			toastError(err);
		}
	};

	const handleDelete = async () => {
		if (!deletingDevice) return;
		try {
			await api.delete(`/flydesk/devices/${deletingDevice.id}`);
			setDevices((prev) => prev.filter((d) => d.id !== deletingDevice.id));
			toast.success(i18n.t("flydeskDevices.toasts.deleted"));
		} catch (err) {
			toastError(err);
		}
		setDeletingDevice(null);
	};

	const term = searchParam.trim().toLowerCase();

	const filtered = useMemo(() => {
		if (!term) return devices;
		return devices.filter((d) =>
			[d.name, d.deviceId, d.company?.name, d.user?.email, d.os]
				.filter(Boolean)
				.some((v) => String(v).toLowerCase().includes(term))
		);
	}, [devices, term]);

	// Agrupa por empresa. Para o super admin, as empresas que têm o módulo
	// mas ainda não registraram nenhum PC também entram — é onde ele vê que
	// falta instalar.
	const groups = useMemo(() => {
		const map = new Map();

		if (isSuper) {
			companies.forEach((c) => {
				map.set(c.id, {
					id: c.id,
					name: c.name,
					maxDevices: c.maxDevices,
					devices: [],
				});
			});
		}

		filtered.forEach((d) => {
			const id = d.companyId;
			if (!map.has(id)) {
				map.set(id, {
					id,
					name: d.company?.name || `Empresa ${id}`,
					maxDevices: d.company?.maxDevices ?? null,
					devices: [],
				});
			}
			map.get(id).devices.push(d);
		});

		let list = Array.from(map.values());
		// Buscando, some quem não teve resultado — senão a busca não filtra nada.
		if (term) list = list.filter((g) => g.devices.length > 0);
		return list.sort((a, b) => a.name.localeCompare(b.name));
	}, [filtered, companies, isSuper, term]);

	const stats = useMemo(() => {
		const online = devices.filter(
			(d) => !d.blocked && isOnline(d.lastSeenAt)
		).length;
		return {
			total: devices.length,
			online,
			blocked: devices.filter((d) => d.blocked).length,
			companies: isSuper
				? companies.length
				: new Set(devices.map((d) => d.companyId)).size,
		};
	}, [devices, companies, isSuper]);

	const toggleGroup = (id) =>
		setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));

	return (
		<MainContainer>
			<ConfirmationModal
				title={i18n.t("flydeskDevices.confirmDelete.title")}
				open={confirmOpen}
				onClose={() => {
					setConfirmOpen(false);
					setDeletingDevice(null);
				}}
				onConfirm={handleDelete}
			>
				{i18n.t("flydeskDevices.confirmDelete.message")}
			</ConfirmationModal>

			<MainHeader>
				<Title>{i18n.t("flydeskDevices.title")}</Title>
				<MainHeaderButtonsWrapper>
					<TextField
						className={classes.search}
						placeholder={i18n.t("flydeskDevices.searchPlaceholder")}
						type="search"
						value={searchParam}
						onChange={(e) => setSearchParam(e.target.value)}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon style={{ color: "#94A3B8", fontSize: 20 }} />
								</InputAdornment>
							),
						}}
					/>
					<Tooltip title={i18n.t("flydeskDevices.refresh")}>
						<IconButton onClick={() => fetchDevices()}>
							<RefreshIcon />
						</IconButton>
					</Tooltip>
				</MainHeaderButtonsWrapper>
			</MainHeader>

			<div className={classes.root}>
				<div className={classes.statsRow}>
					<Paper className={classes.statCard} variant="outlined">
						<span className={classes.statValue}>{stats.total}</span>
						<span className={classes.statLabel}>
							{i18n.t("flydeskDevices.stats.total")}
						</span>
					</Paper>
					<Paper className={classes.statCard} variant="outlined">
						<span className={`${classes.statValue} ${classes.accentOnline}`}>
							{stats.online}
						</span>
						<span className={classes.statLabel}>
							{i18n.t("flydeskDevices.stats.online")}
						</span>
					</Paper>
					<Paper className={classes.statCard} variant="outlined">
						<span className={classes.statValue}>{stats.companies}</span>
						<span className={classes.statLabel}>
							{i18n.t("flydeskDevices.stats.companies")}
						</span>
					</Paper>
					<Paper className={classes.statCard} variant="outlined">
						<span
							className={`${classes.statValue} ${
								stats.blocked ? classes.accentBlocked : ""
							}`}
						>
							{stats.blocked}
						</span>
						<span className={classes.statLabel}>
							{i18n.t("flydeskDevices.stats.blocked")}
						</span>
					</Paper>
				</div>

				<Paper className={classes.installBox} variant="outlined">
					<div className={classes.installText}>
						<Typography variant="subtitle2">
							{i18n.t("flydeskDevices.download.title")}
						</Typography>
						<Typography variant="body2" color="textSecondary">
							{i18n.t("flydeskDevices.download.description")}
						</Typography>
					</div>
					<Button
						variant="contained"
						color="primary"
						startIcon={<GetAppIcon />}
						href={INSTALLER_URL}
					>
						{i18n.t("flydeskDevices.download.button")}
					</Button>
				</Paper>

				{loading && (
					<>
						<div className={classes.skeleton} />
						<div className={classes.skeleton} />
						<div className={classes.skeleton} />
					</>
				)}

				{!loading && groups.length === 0 && (
					<Paper variant="outlined">
						<div className={classes.emptyState}>
							<DesktopWindowsIcon className={classes.emptyIcon} />
							<Typography variant="subtitle1">
								{term
									? i18n.t("flydeskDevices.noResults")
									: i18n.t("flydeskDevices.empty")}
							</Typography>
							{!term && (
								<Typography variant="body2" style={{ marginTop: 6 }}>
									{i18n.t("flydeskDevices.emptyHint")}
								</Typography>
							)}
						</div>
					</Paper>
				)}

				{!loading &&
					groups.map((group) => {
						const open = !collapsed[group.id];
						const onlineCount = group.devices.filter(
							(d) => !d.blocked && isOnline(d.lastSeenAt)
						).length;
						const used = group.devices.length;
						const limit = group.maxDevices;
						const pct = limit ? Math.min(100, (used / limit) * 100) : 0;
						const full = Boolean(limit) && used >= limit;

						return (
							<Paper
								key={group.id}
								className={classes.companyCard}
								variant="outlined"
							>
								<div
									className={classes.companyHeader}
									onClick={() => toggleGroup(group.id)}
								>
									<Avatar
										className={classes.companyAvatar}
										style={{ backgroundColor: colorFor(group.name) }}
									>
										{initialsOf(group.name)}
									</Avatar>

									<div className={classes.companyInfo}>
										<div className={classes.companyName}>{group.name}</div>
										<div className={classes.companyMeta}>
											{i18n.t("flydeskDevices.deviceCount", { n: used })}
											{onlineCount > 0 && (
												<> · {i18n.t("flydeskDevices.onlineCount", { n: onlineCount })}</>
											)}
										</div>
									</div>

									{limit ? (
										<div className={classes.licenceWrap}>
											<span className={classes.licenceLabel}>
												{used}/{limit} {i18n.t("flydeskDevices.licences")}
											</span>
											<LinearProgress
												variant="determinate"
												value={pct}
												className={`${classes.licenceBar} ${
													full ? classes.licenceBarFull : ""
												}`}
											/>
										</div>
									) : (
										<span className={classes.licenceLabel}>
											{i18n.t("flydeskDevices.unlimited")}
										</span>
									)}

									<ExpandMoreIcon
										className={`${classes.chevron} ${
											open ? classes.chevronOpen : ""
										}`}
									/>
								</div>

								<Collapse in={open} timeout="auto" unmountOnExit>
									{group.devices.length === 0 ? (
										<div className={classes.emptyCompany}>
											{i18n.t("flydeskDevices.noDevicesInCompany")}
										</div>
									) : (
										group.devices.map((device) => {
											const online = isOnline(device.lastSeenAt);
											const state = device.blocked
												? "blocked"
												: online
												? "online"
												: "offline";
											const label = device.blocked
												? i18n.t("flydeskDevices.blocked")
												: online
												? i18n.t("flydeskDevices.online")
												: i18n.t("flydeskDevices.offline");

											return (
												<div
													key={device.id}
													className={`${classes.deviceRow} ${
														device.blocked ? classes.deviceRowBlocked : ""
													}`}
												>
													<DesktopWindowsIcon className={classes.deviceIcon} />

													<div className={classes.deviceMain}>
														<div className={classes.deviceName}>
															{device.name || i18n.t("flydeskDevices.unnamed")}
														</div>
														<div className={classes.deviceSub}>
															{device.os || "—"}
															{device.user?.email && <> · {device.user.email}</>}
														</div>
													</div>

													<DeviceId value={device.deviceId} />

													<div className={classes.deviceStatus}>
														<StatusDot state={state} label={label} />
													</div>

													<div className={classes.deviceSeen}>
														{relativeTime(device.lastSeenAt)}
													</div>

													{canManage && (
														<IconButton
															size="small"
															onClick={(e) =>
																setMenu({ anchor: e.currentTarget, device })
															}
														>
															<MoreVertIcon fontSize="small" />
														</IconButton>
													)}
												</div>
											);
										})
									)}
								</Collapse>
							</Paper>
						);
					})}
			</div>

			<Menu
				anchorEl={menu.anchor}
				open={Boolean(menu.anchor)}
				onClose={() => setMenu({ anchor: null, device: null })}
				getContentAnchorEl={null}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "right" }}
			>
				<MenuItem onClick={() => handleToggleBlocked(menu.device)}>
					<ListItemIcon style={{ minWidth: 34 }}>
						{menu.device?.blocked ? (
							<LockOpenIcon fontSize="small" />
						) : (
							<BlockIcon fontSize="small" />
						)}
					</ListItemIcon>
					{menu.device?.blocked
						? i18n.t("flydeskDevices.actions.unblock")
						: i18n.t("flydeskDevices.actions.block")}
				</MenuItem>
				<Divider />
				<MenuItem
					onClick={() => {
						setDeletingDevice(menu.device);
						setConfirmOpen(true);
						setMenu({ anchor: null, device: null });
					}}
				>
					<ListItemIcon style={{ minWidth: 34 }}>
						<DeleteOutlineIcon fontSize="small" />
					</ListItemIcon>
					{i18n.t("flydeskDevices.actions.delete")}
				</MenuItem>
			</Menu>
		</MainContainer>
	);
};

export default FlydeskDevices;
